import "./service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";
import Icon from "../../components/Icon";
import useReveal from "../../hooks/useReveal";

const offerings = [
  {
    icon: "mobile",
    title: "Cross-Platform Mobile Applications",
    desc: "Build modern Android and iOS applications using a single scalable codebase without compromising performance, responsiveness, or native user experience.",
  },
  {
    icon: "cart",
    title: "E-Commerce Mobile Applications",
    desc: "Develop scalable e-commerce mobile apps with secure payments, product management, order tracking, customer analytics, and optimized mobile shopping experiences.",
  },
  {
    icon: "layers",
    title: "AI-Powered Mobile Solutions",
    desc: "Integrate AI-powered features into mobile applications including smart recommendations, intelligent search, AI assistants, automation workflows, and personalized user experiences.",
  },
  {
    icon: "clock",
    title: "Real-Time Mobile Applications",
    desc: "Build real-time mobile platforms with live updates, instant notifications, collaborative systems, and event-driven architectures.",
  },
  {
    icon: "users",
    title: "Mobile UI/UX Design",
    desc: "Create modern, user-focused mobile interfaces with smooth interactions, intuitive navigation, scalable design systems, and platform-consistent experiences.",
  },
];

const techGroups = [
  {
    label: "Mobile Frameworks",
    items: [
      "React Native CLI",
      "Expo",
      "TypeScript",
      "Native Modules",
      "React Navigation",
    ],
  },
  {
    label: "Backend & Cloud",
    items: ["Firebase", "Supabase", "Node.js", "FastAPI", "AWS Services"],
  },
  {
    label: "APIs & Integrations",
    items: [
      "REST APIs",
      "GraphQL",
      "Push Notification Services",
      "Payment Gateway APIs",
      "Social Authentication APIs",
    ],
  },
];

const keyFeatures = [
  {
    icon: "bolt",
    title: "Fast & Smooth Performance",
    desc: "Applications optimized for high responsiveness, smooth animations, reduced loading times, and excellent runtime performance across Android and iOS devices.",
  },
  {
    icon: "cloud",
    title: "Offline Support & Synchronization",
    desc: "Enable mobile applications to function even without internet connectivity using local storage, background sync, and offline-first architecture.",
  },
  {
    icon: "chat",
    title: "Push Notifications & Engagement",
    desc: "Implement real-time push notification systems for user engagement, transactional alerts, reminders, onboarding, and communication workflows.",
  },
  {
    icon: "shield",
    title: "Secure Mobile Data Handling",
    desc: "Protect user data using secure authentication systems, encrypted storage, token security, and enterprise-grade security standards.",
  },
];

export default function MobileApplication() {
  const [offeringsRef, offeringsVisible] = useReveal();
  const [featuresRef, featuresVisible] = useReveal();

  return (
    <>
      <SEO
        title="Mobile App Development: Android & iOS | Best Code Technology"
        description="We develop high-performance Android and iOS mobile applications using modern frameworks."
        keywords="mobile app development, Android apps, iOS apps, React Native"
        url="https://bestcodetechnology.in/services/mobile-applications"
      />

      <div className="svc-page">
        {/* ── Hero ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <h1 className="svc-hero__headline">
                  Build Powerful Mobile Applications for{" "}
                  <span>Android &amp; iOS</span>
                </h1>
                <p className="svc-hero__desc">
                  We create high-performance mobile applications with seamless
                  user experiences, scalable backend systems, and
                  production-ready architecture. From startup MVPs to enterprise
                  mobile platforms, we deliver powerful apps engineered for
                  speed, reliability, and long-term scalability.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── What We Offer ── */}
        <section className="svc-offerings">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">
                  What We Offer
                </span>
                <h2 className="svc-section-title">Mobile Engineering</h2>
                <p className="svc-section-sub">
                  We provide end-to-end mobile application development services
                  using modern cross-platform frameworks, scalable backend
                  infrastructure, cloud services, and enterprise-grade
                  engineering practices.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={offeringsRef}>
              {offerings.map((item, i) => (
                <div
                  key={item.title}
                  className={`col-12 col-md-6 col-xl-4 reveal ${offeringsVisible ? "" : "is-hidden"}`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-offer-card h-100">
                    <div className="svc-offer-card__header">
                      <div className="svc-offer-card__icon">
                        <Icon name={item.icon} size={22} />
                      </div>
                    </div>
                    <div
                      className="svc-offer-card__divider"
                      aria-hidden="true"
                    />
                    <h4 className="svc-offer-card__title">{item.title}</h4>
                    <p className="svc-offer-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Tech Stack ── */}
        <section className="svc-tech">
          <div className="container-xl">
            <div className="row align-items-start g-5">
              <div className="col-12 col-lg-4 svc-tech__col-left pe-lg-5">
                <span className="pill-label svc-section-label">
                  Technologies
                </span>
                <h2 className="svc-section-title">
                  Our Mobile Technology Stack
                </h2>
                <p className="svc-section-sub svc-section-sub--left">
                  Modern mobile technologies selected for scalability, developer
                  productivity, performance optimization, and enterprise-grade
                  reliability.
                </p>
              </div>

              <div className="col-12 col-lg-8">
                <div className="svc-tech__groups">
                  {techGroups.map((group) => (
                    <div key={group.label} className="svc-tech__group">
                      <span className="svc-tech__group-label">
                        {group.label}
                      </span>
                      <div className="svc-tech__badges">
                        {group.items.map((tech) => (
                          <span key={tech} className="svc-tech__badge">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Key Features ── */}
        <section className="svc-usecases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label svc-section-label">
                  Key Features
                </span>
                <h2 className="svc-section-title">
                  Engineered for the Real World
                </h2>
                <p className="svc-section-sub">
                  Every mobile application we build is optimized for
                  performance, reliability, security, and production-scale usage
                  across real-world environments.
                </p>
              </div>
            </div>

            <div className="row g-4 reveal-stagger" ref={featuresRef}>
              {keyFeatures.map((feat, i) => (
                <div
                  key={feat.title}
                  className={`col-12 col-lg-6 reveal ${
                    featuresVisible ? "" : "is-hidden"
                  }`}
                  style={{ "--stagger-index": i }}
                >
                  <div className="svc-usecase-card h-100">
                    <div className="svc-usecase-card__accent">
                      <Icon name={feat.icon} size={22} />
                    </div>
                    <div className="svc-usecase-card__body">
                      <h3 className="svc-usecase-card__title">{feat.title}</h3>
                      <p className="svc-usecase-card__desc">{feat.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
